import { QuoteType } from '../types/master-data.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2 } from 'lucide-react';

interface QuoteTypeTableProps {
  quoteTypes: QuoteType[];
  onEdit: (quoteType: QuoteType) => void;
  onDelete: (quoteType: QuoteType) => void;
  isLoading?: boolean;
}

export function QuoteTypeTable({ quoteTypes, onEdit, onDelete, isLoading }: QuoteTypeTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cargando tipos de cotización...</p>
      </div>
    );
  }

  if (!quoteTypes || quoteTypes.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-medium">No hay tipos de cotización configurados</p>
        <p className="text-xs text-muted-foreground mt-1">
          Agrega tipos de cotización para clasificar y organizar las cotizaciones del sistema
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Prefijo</TableHead>
            <TableHead>Descripción</TableHead>
            <TableHead>Tipo Upstream</TableHead>
            <TableHead>Orden</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {quoteTypes.map((quoteType) => (
            <TableRow key={quoteType.id}>
              <TableCell className="font-mono font-semibold">{quoteType.code}</TableCell>
              <TableCell>{quoteType.name}</TableCell>
              <TableCell className="font-mono">{quoteType.codePrefix}</TableCell>
              <TableCell className="max-w-[200px] truncate text-muted-foreground">
                {quoteType.description || '-'}
              </TableCell>
              <TableCell>
                {quoteType.upstreamType ? (
                  <Badge variant="outline">{quoteType.upstreamType.name}</Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>{quoteType.order}</TableCell>
              <TableCell>
                <Badge variant={quoteType.isActive ? 'success' : 'default'}>
                  {quoteType.isActive ? 'Activo' : 'Inactivo'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(quoteType)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar tipo de cotización</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(quoteType)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Eliminar tipo de cotización</TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default QuoteTypeTable;
